<template>
  <router-view v-slot="{Component, route: matchedRoute}">
    <keep-alive>
      <component
        :is="layout"
        :key="layoutName">
        <keep-alive>
          <component
            :is="Component"
            :key="matchedRoute.matched[0]?.path || matchedRoute.name" />
        </keep-alive>
      </component>
    </keep-alive>
  </router-view>
</template>

<script setup>
import {computed, onBeforeUnmount} from 'vue'
import {useRoute} from 'vue-router'
import {provideNavigation} from '@composables'
import LayoutDefault from '@layouts/layout-default.vue'
import LayoutPlain from '@layouts/layout-plain.vue'

defineOptions({
  name: 'App',
})

const route = useRoute()

// provideNavigation
// - Must stay here, not in the layout: the routed pages that call
//   useNavigation() are slot content, so their inject resolves against
//   App's component tree rather than the layout's.
const navigationContext = provideNavigation()

onBeforeUnmount(() => {
  navigationContext?.cleanup?.()
})

const LAYOUTS = {
  default: LayoutDefault,
  plain: LayoutPlain,
}

// layoutName
// - Pick the layout key from route meta. Walk matched records
//   deepest-first because vue-router does not merge `meta` down the
//   chain, so a parent route's `layout` covers its children. Defaults
//   to 'default'.
const layoutName = computed(() => {
  const record = [...route.matched]
    .reverse()
    .find((r) => LAYOUTS[r.meta?.layout])

  return record ? record.meta.layout : 'default'
})

// layout
const layout = computed(() => {
  return LAYOUTS[layoutName.value]
})

// Nested keep-alive note:
// - The outer keep-alive caches the layout instances, so switching
//   between default and plain deactivates (not destroys) the previous
//   layout. This keeps the inner page cache alive across a layout
//   boundary, e.g. car detail survives a round-trip into an inspection
//   report (no refetch, scroll preserved).
// - The inner keep-alive is the original page cache, keyed by the
//   top-level matched path.
</script>
